import { useState, useEffect } from "react";
import {
  Search, Upload, Download, X, Users, Trophy, RotateCcw, Filter, ChevronDown,
} from "lucide-react";
import { useOptions } from "../contexts/OptionsContext";
import {
  getStudents,
  deleteStudent,
  searchStudentById,
} from "../api/services/studentsService";
import UploadCSVModal from "../components/modals/UploadCSVModal";
import EditStudentModal from "../components/modals/EditStudentModal";
import DeleteStudentConfirmModal from "../components/modals/DeleteStudentConfirmModal";
import StudentsEvaluationResults from "./StudentsEvaluationResults";
import ColumnFilterDropdown from "../components/ColumnFilterDropdown";
import { DEFAULT_PROFILE_FILTERS, PROFILE_FILTER_MAP, ADMIN_EMAILS } from "../utils/constants";
import { toTitleCase, isAdminUser } from "../utils/helpers";


function Students() {
  const {
    departments,
    years: yearOptions,
    semesters: semesterOptions,
    getDepartmentLabel,
    getYearLabel,
    getSemesterLabel,
  } = useOptions();

  const [activeTab, setActiveTab] = useState("all");
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total_records: 0, total_pages: 1 });
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_PROFILE_FILTERS);

  const isAdmin = isAdminUser(localStorage.getItem("userEmail"));

  const optionsMap = {
    departments: departments || [],
    years: yearOptions || [],
    semesters: semesterOptions || [],
  };

  const hasActiveFilters = Object.keys(DEFAULT_PROFILE_FILTERS).some((key) => filters[key] !== DEFAULT_PROFILE_FILTERS[key]);

  const handleFilterApply = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
    setIsSearchMode(false);
    setSearchQuery("");
    fetchStudents(1, updated);
  };

  const fetchStudents = async (page = 1, currentFilters) => {
    if (activeTab !== "all") return;
    try {
      setLoading(true);
      const pf = currentFilters || filters;
      const params = { page, page_size: 10 };
      if (pf.branch !== "All Branches") params.branch = pf.branch;
      if (pf.year !== "All Years") params.year = pf.year;
      if (pf.semester !== "All Semesters") params.semester = pf.semester;
      const r = await getStudents(params);
      if (r.success) {
        const data = r.data.results || [];
        setStudents(data);
        setFilteredStudents(data);
        setPagination({ page: r.data.page, page_size: r.data.page_size || 10, total_records: r.data.total_records, total_pages: r.data.total_pages });
      }
    } catch (e) {
      console.error("Error fetching students:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (activeTab === "all") fetchStudents(1, DEFAULT_PROFILE_FILTERS); }, [activeTab]);

  const handleSearch = async (query) => {
    if (!query.trim()) { fetchStudents(1, filters); return; }
    setFilters(DEFAULT_PROFILE_FILTERS);
    setIsSearchMode(true);
    try {
      setIsSearching(true);
      setLoading(true);
      const r = await searchStudentById(query.trim());
      if (r.success && r.data.student) { setStudents([r.data.student]); setFilteredStudents([r.data.student]); }
      else { setStudents([]); setFilteredStudents([]); }
    } catch (e) { console.error("Search error:", e); }
    finally { setLoading(false); setIsSearching(false); }
  };

  const handleTabChange = (tab) => { setActiveTab(tab); setSearchQuery(""); setIsSearchMode(false); };
  const clearAllFilters = () => { setSearchQuery(""); setFilters(DEFAULT_PROFILE_FILTERS); fetchStudents(1, DEFAULT_PROFILE_FILTERS); };
  const clearFilter = (key, defaultValue) => { const updated = { ...filters, [key]: defaultValue }; setFilters(updated); fetchStudents(1, updated); };
  const handleUploadSuccess = () => fetchStudents(1, filters);
  const handleEditStudent = (s) => setEditingStudent(s);
  const handleDeleteStudent = (s) => setDeletingStudent(s);
  const handleUpdateStudent = (updated) => { setStudents((prev) => prev.map((s) => s._id === updated._id || s.student_id === updated.student_id ? { ...s, ...updated } : s)); setEditingStudent(null); };
  const handleConfirmDelete = async () => {
    if (!deletingStudent) return;
    try {
      const id = deletingStudent._id || deletingStudent.id || deletingStudent.student_id;
      const r = await deleteStudent(id);
      if (r.success) { setStudents((prev) => prev.filter((s) => s._id !== id && s.id !== id && s.student_id !== id)); setDeletingStudent(null); }
    } catch (e) { console.error("Error deleting student:", e); }
  };
  const downloadTemplate = () => {
    const csv = `student_name,student_id,email,degree,department,year,semester\nJohn Doe,STU001,john.doe@university.edu,Masters,Computer Science,Year 3,Semester 5`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "student_template.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col p-6 bg-screen-bg h-full overflow-hidden">
      {/* ── Header ── */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text-main">Students</h1>
          <p className="text-text-sub text-sm mt-0.5">Manage your university students</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={downloadTemplate}
              className="px-3 py-1.5 rounded-lg bg-primary border border-purple-glow-20 text-btn-text text-sm flex items-center gap-1.5 hover:opacity-90 transition-colors cursor-pointer">
              <Download size={15} /> Template
            </button>
            <button onClick={() => setShowUploadModal(true)}
              className="px-3 py-1.5 rounded-lg bg-primary text-btn-text text-sm font-medium flex items-center gap-1.5 hover:opacity-90 transition-all cursor-pointer">
              <Upload size={15} /> Upload Students
            </button>
          </div>
        )}
      </div>

      {/* ── Tabs ── */}
      <nav className="adm-nav">
        {[
          { key: "all", label: "Profiles", icon: Users },
          { key: "results", label: "Evaluation Results", icon: Trophy },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => handleTabChange(key)}
            className={`adm-tab ${activeTab === key ? "active" : ""}`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </nav>

      {/* ── Profiles Tab ── */}
      {activeTab === "all" ? (
        <>
          {/* Action Bar — consolidated row */}
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1 max-w-xs">
              <Search size={16} className="absolute left-3 top-1/4 -translate-y-1/2 text-text-sub" />
              <input type="text" autoComplete="off" placeholder="Search by Student ID..."
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(searchQuery); }}
                className="w-full pl-9 pr-8 py-2 rounded-lg text-sm text-text-main placeholder:text-text-sub outline-none transition-colors"
                style={{ background: "var(--screen-background-color)", color: "var(--text-primary)", border: "2px solid rgba(190, 5, 250, 0.2)" }}
                onFocus={(e) => e.target.style.borderColor = "var(--buttons-background-color)"}
                onBlur={(e) => e.target.style.borderColor = "rgba(190, 5, 250, 0.2)"} />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(""); setIsSearchMode(false); setFilters(DEFAULT_PROFILE_FILTERS); fetchStudents(1, DEFAULT_PROFILE_FILTERS); }}
                  className="absolute right-2 top-1/4 -translate-y-1/2 p-0.5 -mt-1 rounded-full bg-primary text-btn-text cursor-pointer">
                  <X size={12} />
                </button>
              )}
            </div>
            <button onClick={() => handleSearch(searchQuery)} disabled={!searchQuery.trim()}
              className="px-3 py-2 rounded-lg bg-primary text-btn-text text-sm font-medium cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-all">
              Search
            </button>

            {hasActiveFilters && (
              <button onClick={clearAllFilters}
                className="px-3 py-2 rounded-lg text-sm text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 transition-colors cursor-pointer flex items-center gap-1">
                <RotateCcw size={13} /> Clear
              </button>
            )}
          </div>

          {/* Expandable filter chips */}
          {hasActiveFilters && (
            <div className="mb-3 flex flex-wrap items-center gap-2 animate-in">
              {filters.branch !== "All Branches" && <Chip label={getDepartmentLabel(filters.branch)} onClear={() => clearFilter("branch", "All Branches")} />}
              {filters.year !== "All Years" && <Chip label={getYearLabel(filters.year)} onClear={() => clearFilter("year", "All Years")} />}
              {filters.semester !== "All Semesters" && <Chip label={getSemesterLabel(filters.semester)} onClear={() => clearFilter("semester", "All Semesters")} />}
            </div>
          )}

          {/* Table */}
          <div className="bg-card-bg rounded-xl border border-purple-glow-10 flex flex-col flex-1 min-h-0 overflow-hidden shadow-sm">
            <div className="overflow-auto flex-1">
              <table className="w-full min-w-max">
                <thead className="adm-thead sticky top-0 z-10">
                  <tr>
                    {["STUDENT ID", "NAME", "EMAIL", "BRANCH", "GRADUATION YEAR", "SEMESTER"].map((h) => {
                      const fConfig = PROFILE_FILTER_MAP[h];
                      return (
                        <th key={h} className="adm-th">
                          <span className="inline-flex items-center gap-1">
                            {h}
                            {fConfig && optionsMap[fConfig.optionsKey] && (
                              <ColumnFilterDropdown column={h} options={optionsMap[fConfig.optionsKey]}
                                currentValue={filters[fConfig.key]} defaultVal={fConfig.defaultVal}
                                onApply={(val) => handleFilterApply(fConfig.key, val)} />
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-7 h-7 rounded-full border-[3px] border-purple-500/20 border-t-purple-500 animate-spin" />
                          <p className="text-text-sub text-sm">Loading...</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-text-sub text-sm">
                        {hasActiveFilters ? "No students found matching your filters" : "No students found. Upload a CSV file to add students."}
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map((s, i) => (
                      <tr key={s._id || s.id || i} className="adm-tr">
                        <td className="adm-td font-medium text-text-main">{s.student_id}</td>
                        <td className="adm-td font-medium text-text-main">{s.name || s.student_name}</td>
                        <td className="adm-td text-text-sub">{s.email}</td>
                        <td className="adm-td text-text-sub">{toTitleCase(s.branch)}</td>
                        <td className="adm-td text-text-sub">{s.year}</td>
                        <td className="adm-td text-text-sub">{s.semester}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 0 && !isSearchMode && (
  <div className="flex items-center justify-between px-5 py-3" style={{ borderTop: "1px solid rgba(190, 5, 250, 0.1)" }}>
    <p className="text-sm text-text-main">
      Showing <span className="font-semibold">{(pagination.page - 1) * pagination.page_size + 1}–{Math.min(pagination.page * pagination.page_size, pagination.total_records)}</span> of <span className="font-semibold">{pagination.total_records}</span> students
    </p>
    <div className="flex items-center gap-2">
      <button disabled={pagination.page === 1} onClick={() => fetchStudents(pagination.page - 1, filters)}
        className="px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        style={{ background: "var(--buttons-background-color)", color: "white", border: "none" }}>
        Prev
      </button>
      <span className="text-sm text-text-main font-medium px-3">
        Page {pagination.page} of {pagination.total_pages}
      </span>
      <button disabled={pagination.page === pagination.total_pages} onClick={() => fetchStudents(pagination.page + 1, filters)}
        className="px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        style={{ background: "var(--buttons-background-color)", color: "white", border: "none" }}>
        Next
      </button>
    </div>
  </div>
)}
          </div>
        </>
      ) : (
        <StudentsEvaluationResults />
      )}

      {/* Modals */}
      {showUploadModal && <UploadCSVModal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} onSuccess={handleUploadSuccess} />}
      {editingStudent && <EditStudentModal isOpen={!!editingStudent} student={editingStudent} onClose={() => setEditingStudent(null)} onSave={handleUpdateStudent} />}
      {deletingStudent && <DeleteStudentConfirmModal isOpen={!!deletingStudent} student={deletingStudent} onClose={() => setDeletingStudent(null)} onConfirm={handleConfirmDelete} />}
    </div>
  );
}

function Chip({ label, onClear }) {
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary text-btn-text border border-primary/20">
      <span>{label}</span>
      <button onClick={onClear} className="hover:bg-primary/20 rounded-full p-0.5 cursor-pointer"><X size={11} /></button>
    </div>
  );
}

export default Students;